import React from 'react';
import { useTranslation } from 'react-i18next';

import UserProfileSticky from './UserProfileSticky';
import { useHeaderLayoutCompanyData } from '../hooks/useHeaderLayoutCompanyData';
import { useInviteOwnerModal } from '../hooks/useInviteOwnerModal';
import { useAppSelector } from '../stores';
import { switchActiveCompany } from '../utils/switchActiveCompany';
import {
    ApprovalRequestModal,
    ChangeCompanyModal,
    InviteOwnerModal,
} from '../views/modal';

type Props = {
    /** When false, hides « invite owner » (e.g. contributor-only sidebar). Default: true. */
    showInviteOwnerInMenu?: boolean;
    /** Changement de compagnie — true uniquement sur la sidebar Insights. Default: false. */
    showChangeCompanyInMenu?: boolean;
};

/**
 * Bottom-of-sidebar user menu — same as process interview (UserProfileSticky + modals).
 */
const SidebarUserProfileSection = ({
    showInviteOwnerInMenu = true,
    showChangeCompanyInMenu = false,
}: Props) => {
    const { t } = useTranslation();
    const users = useAppSelector((state) => state.auth);
    const company = useAppSelector((state) => state.company.company);
    const role = useAppSelector((state) => state.role.role);
    const {
        loadcompany,
        listApproval,
        loading,
        companyById,
        myCompanies,
        creditsAvailable,
        creditsLoading,
        companyId,
        accessToken,
    } = useHeaderLayoutCompanyData({ includeMyCompanies: showChangeCompanyInMenu });
    const inviteOwner = useInviteOwnerModal(t);

    const companyPlanStatus = companyById?.status ?? company?.status;
    const planLabel = companyPlanStatus
        ? t(`layout.header.plan${companyPlanStatus.toUpperCase()}`)
        : t('layout.header.planUnknown');
    const fullname = (users.user.fullname || '').trim() || users.user.email || '';
    const isAdmin = role === 'ADMIN' || role === 'admin';

    const onSwitchCompany = async (id: string | undefined) => {
        if (!id || !accessToken) return;
        await switchActiveCompany(id, accessToken, { reload: true });
    };

    return (
        <>
            <UserProfileSticky
                t={t}
                loadcompany={loadcompany}
                fullname={fullname}
                email={users.user.email || ''}
                role={role}
                planLabel={planLabel}
                creditsAvailable={creditsAvailable}
                creditsLoading={creditsLoading}
                isAdmin={isAdmin}
                showInviteOwnerInMenu={showInviteOwnerInMenu}
                showChangeCompanyInMenu={showChangeCompanyInMenu}
            />
            <InviteOwnerModal
                alert={inviteOwner.alert}
                email={inviteOwner.email}
                isSubmitting={inviteOwner.isSubmitting}
                handleChange={inviteOwner.handleChange}
                handleSubmit={inviteOwner.handleSubmit}
                t={t}
            />
            {showChangeCompanyInMenu ? (
                <ChangeCompanyModal
                    companies={myCompanies}
                    currentCompanyId={companyId || ''}
                    onSave={onSwitchCompany}
                    t={t}
                />
            ) : null}
            <ApprovalRequestModal approvalList={listApproval} loading={loading} />
        </>
    );
};

export default SidebarUserProfileSection;
